// Walks member bodies, working out the type of each expression as far as the code and the
// framework table tell, and records each call, creation and member access whose target is declared
// in the code: exact when the receiver's type is known, a guess when only the name is.
import type { Arg, CsAttribute, Expr, LambdaExpr, Pattern, Span, Stmt, TypeConstraint, TypeRef } from '../csharp/ast';
import { lex } from '../csharp/lexer';
import { PREDEFINED_TYPES } from '../csharp/parser';
import { awaited, checksFake, configuresFake, delegateParams, delegateResult, elementType, externalCall, externalLambdaParams, externalMember, FAKE_TYPES, fakedBy, fakingLambda, httpMethodOf, implementsInterface, keepsValues } from './framework';
import type { CodeCall, CodeEndpoint, CodeHandoff, CodeRequest, CodeUse } from './model';
import { urlPattern } from './requests';
import { ext, OPAQUE, substitute, SymbolTable, TypeSymbol, UNKNOWN, type MemberSymbol, type ParsedCodeFile, type Subst, type Ty, type Where } from './symbols';

class Scope {
  private readonly vars = new Map<string, Ty>();
  /** Local functions declared here, with their return types. */
  private readonly fns = new Map<string, Ty>();

  constructor(readonly parent: Scope | null) {}

  get(name: string): Ty | undefined {
    return this.vars.get(name) ?? this.parent?.get(name);
  }

  fn(name: string): Ty | undefined {
    return this.fns.get(name) ?? this.parent?.fn(name);
  }

  set(name: string, ty: Ty): void {
    this.vars.set(name, ty);
  }

  setFn(name: string, ty: Ty): void {
    this.fns.set(name, ty);
  }

  child(): Scope {
    return new Scope(this);
  }
}

interface Candidate {
  member: MemberSymbol;
  subst: Subst;
}

interface Picked extends Candidate {
  ambiguous: boolean;
}

/** Words that look like calls in code the parser could not read, but are not. */
const NOT_CALLS = new Set([
  'if', 'while', 'for', 'foreach', 'switch', 'catch', 'using', 'lock', 'fixed', 'return', 'typeof', 'nameof', 'sizeof',
  'default', 'when', 'checked', 'unchecked', 'await', 'throw', 'new', 'base', 'this', 'var', 'is', 'as', 'in', 'out', 'ref',
]);

const COMPARISON = new Set(['==', '!=', '<', '>', '<=', '>=', '&&', '||']);

/** Framework types minimal API routes are mapped on, and handed to methods that map more. */
const ROUTE_BUILDERS = new Set(['IEndpointRouteBuilder', 'RouteGroupBuilder', 'WebApplication']);

/** A parameter of a method: the route builder it is given. */
export interface RouteParam {
  member: string;
  index: number;
}

/** Where routes mapped on a builder start: its group's prefix, after the prefix of the builder a method was given. */
interface RouteBase {
  prefix: string;
  from?: RouteParam;
}

/** Minimal API calls that map an endpoint, and the methods each one takes (`MapMethods` names them). */
const MAP_METHODS: Record<string, string[]> = {
  MapGet: ['GET'],
  MapPost: ['POST'],
  MapPut: ['PUT'],
  MapDelete: ['DELETE'],
  MapPatch: ['PATCH'],
  MapMethods: [],
  Map: [],
};

/** Calls on any value that compare rather than hand a value over. */
const COMPARES = new Set(['Equals', 'ReferenceEquals', 'CompareTo', 'GetHashCode', 'ToString']);

export class Walker {
  readonly calls: CodeCall[] = [];
  /** Values of the code's types handed to the framework or a package. */
  readonly handoffs: CodeHandoff[] = [];
  /** HTTP requests sent with an HttpClient (their endpoints are matched once all are known). */
  readonly requests: CodeRequest[] = [];
  /** `new HttpRequestMessage(HttpMethod.Post, url)` values, and the request each one is. */
  private readonly requestMessages = new WeakMap<Ty, { method: string; url: string }>();
  /** Strings kept in a variable whose text is known: `var url = $"/orders/{id}"`. */
  private readonly urls = new WeakMap<Ty, string>();
  /** The URL each helper returns, when its code is only that. */
  private readonly returnedUrls = new Map<MemberSymbol, string | null>();
  readonly uses = new Map<string, CodeUse>();
  /** Minimal API endpoints met while walking. */
  readonly endpoints: CodeEndpoint[] = [];
  /** Endpoint ids taken, so two endpoints with the same method and route stay apart. */
  readonly endpointIds = new Set<string>();
  /** The endpoint whose handler lambda is being walked. */
  private endpoint: string | null = null;
  /** Walking code that sets up or checks a fake (`mock.Setup(r => ...)`): its calls are marked `mock`. */
  private mocking = false;
  /** The call or access recorded for an expression, to mark it when a later call shows it configures a fake. */
  private readonly edgeOf = new WeakMap<Expr, CodeCall>();
  /** Route prefixes of the route group values made by `MapGroup`. */
  private readonly groups = new WeakMap<Ty, RouteBase>();
  /** Route builders a method is given (`this IEndpointRouteBuilder app`): its callers' groups complete the routes mapped on them. */
  private readonly builderParams = new WeakMap<Ty, RouteParam>();
  /** Endpoints mapped on a builder a method is given. */
  private readonly endpointsVia = new Map<CodeEndpoint, RouteParam>();
  /** Calls handing a route builder to a method: `api.MapAuthEndpoints()` after `var api = app.MapGroup("/api")`. */
  private readonly routeCalls: (RouteBase & { to: RouteParam })[] = [];
  external = 0;
  unresolved = 0;

  private from = '';
  private file!: ParsedCodeFile;
  private where!: Where;
  private owner: TypeSymbol | null = null;
  /** Namespaces whose extension methods are visible in the current file. */
  private visible = new Set<string>();
  private returnSink: Ty[] | null = null;
  private expectedReturn: Ty = UNKNOWN;
  private constraints = new Map<string, TypeRef[]>();
  private readonly valueTypes = new Map<MemberSymbol, Ty>();
  private readonly paramTypes = new Map<MemberSymbol, Ty[]>();

  constructor(private readonly table: SymbolTable) {}

  // ---------------------------------------------------------------- entry points

  /** A type's own code: its bases, attributes and a primary constructor's call to the base constructor. */
  walkType(sym: TypeSymbol, files: Map<string, ParsedCodeFile>): void {
    const bases = this.table.basesOf(sym);
    for (const part of sym.parts) {
      this.enter(sym.id, files.get(part.ctx.path)!, { ctx: part.ctx, type: sym, typeParams: [] }, sym);
      this.constraints = this.constraintsAround(sym, undefined);
      const scope = this.baseScope(part.cls.primaryParams, false);
      part.cls.baseTypes.forEach((ref) => {
        const ty = this.resolve(ref, false);
        if (ty.k === 'code') this.use(ty.sym, ty.sym.kind === 'interface' ? 'interface' : 'base', ref.span);
        this.useInside(ty, ref.span, ty);
      });
      this.attributes(part.cls.attributes);
      if (part.cls.baseArgs) this.constructorCall(bases.base, part.cls.baseArgs, scope, part.cls.baseTypes[0]!.span);
    }
  }

  /** `: base(...)`, `: this(...)` or a primary constructor's `: Base(...)`: a call to a constructor of `target`. */
  private constructorCall(target: Ty | null, args: Arg[], scope: Scope, span: Span): void {
    const argTypes = args.map((a) => (a.value.kind === 'lambda' ? UNKNOWN : this.argType(a, scope)));
    let picked: Picked | null = null;
    if (target?.k === 'code') {
      const level = this.table.hierarchy(target)[0]!;
      const ctors = this.ctorsOf(level.sym, level.subst);
      picked = ctors.length ? this.pick([ctors], argTypes.length, argTypes, false) : null;
      // No constructor declared (or a primary constructor): the type itself.
      if (picked) this.edge(picked.member.id, 'call', picked.ambiguous ? 'guess' : 'exact', span);
      else if (!ctors.length) this.edge(level.sym.id, 'call', 'exact', span);
    }
    this.walkLambdaArgs(args, scope, picked);
  }

  walkMember(m: MemberSymbol, file: ParsedCodeFile): void {
    this.enter(m.id, file, { ctx: m.ctx, type: m.owner, typeParams: m.typeParams }, m.owner);
    this.constraints = this.constraintsAround(m.owner, m.decl.kind === 'method' ? m.decl.method.constraints : undefined);
    // A positional record's parameters are its properties inside its members.
    const scope = this.baseScope(m.cls.primaryParams, m.cls.keyword === 'record');
    if (m.valueType) this.useType(m.valueType);
    m.params.forEach((p, index) => {
      let ty = p.type ? this.useType(p.type) : UNKNOWN;
      // A route builder parameter gets a value of its own, to know routes mapped on it later.
      if (ty.k === 'ext' && ROUTE_BUILDERS.has(ty.name)) {
        ty = { ...ty };
        this.builderParams.set(ty, { member: m.id, index });
      }
      scope.set(p.name, ty);
    });
    const decl = m.decl;
    switch (decl.kind) {
      case 'method': {
        const method = decl.method;
        this.attributes(method.attributes);
        if (method.initializer) {
          const target = method.initializer.kind === 'base' ? this.table.basesOf(m.owner).base : this.self();
          this.constructorCall(target, method.initializer.args, scope, method.initializer.span);
        }
        const returns = method.returnType ? this.resolve(method.returnType, false) : UNKNOWN;
        if (method.body) this.withReturns(method.modifiers.includes('async') ? awaited(returns) : returns, () => this.stmts(method.body!, scope.child()));
        break;
      }
      case 'property': {
        const p = decl.property;
        this.attributes(p.attributes);
        const valueTy = this.resolve(p.type, false);
        if (p.expressionBody) this.expr(p.expressionBody, scope, valueTy);
        for (const a of p.accessors) {
          if (!a.body) continue;
          const inner = scope.child();
          if (a.kind !== 'get') inner.set('value', valueTy);
          this.withReturns(a.kind === 'get' ? valueTy : UNKNOWN, () => this.stmts(a.body!, inner));
        }
        if (p.initializer) this.expr(p.initializer, scope, valueTy);
        break;
      }
      case 'field':
        if (decl.init) this.expr(decl.init, scope, this.resolve(decl.type, false));
        break;
      case 'enumMember':
        if (decl.value) this.expr(decl.value, scope);
        break;
      case 'recordParam':
        break;
    }
  }

  private enter(from: string, file: ParsedCodeFile, where: Where, owner: TypeSymbol | null): void {
    if (this.file !== file) {
      this.file = file;
      this.visible = new Set(['', ...where.ctx.usings]);
    }
    this.from = from;
    this.where = where;
    this.owner = owner;
    let outermost = owner;
    while (outermost?.outer) outermost = outermost.outer;
    for (let ns = outermost?.namespace ?? null; ns; ns = ns.includes('.') ? ns.slice(0, ns.lastIndexOf('.')) : null) this.visible.add(ns);
  }

  /** Primary constructor parameters are in scope in every member of the class (not of a record, where they are properties). */
  private baseScope(primary: { name: string; type: TypeRef | null }[] | null, record: boolean): Scope {
    const scope = new Scope(null);
    if (!record) for (const p of primary ?? []) scope.set(p.name, p.type ? this.resolve(p.type, false) : UNKNOWN);
    return scope;
  }

  /** Constraints of the type parameters in scope: the method's, then those of the types around it. */
  private constraintsAround(owner: TypeSymbol | null, own: TypeConstraint[] | undefined): Map<string, TypeRef[]> {
    const map = new Map<string, TypeRef[]>();
    for (const c of own ?? []) map.set(c.param, c.types);
    for (let t = owner; t; t = t.outer) for (const part of t.parts) for (const c of part.cls.constraints ?? []) if (!map.has(c.param)) map.set(c.param, c.types);
    return map;
  }

  /**
   * What a value of a type parameter can be used as: the constraint that has `member`, else its
   * first constraint from the code (`where T : Entity`). Other types are returned as they are.
   */
  private constrained(ty: Ty, member: string): Ty {
    if (ty.k !== 'param') return ty;
    const refs = this.constraints.get(ty.name);
    if (!refs?.length) return ty;
    const types = refs.map((r) => this.resolve(r, false));
    return (
      types.find((t) => t.k === 'code' && this.table.hierarchy(t).some((h) => h.sym.byName.has(member))) ??
      types.find((t) => t.k === 'code') ??
      types[0] ??
      ty
    );
  }

  private self(): Ty {
    return this.owner ? { k: 'code', sym: this.owner, args: this.owner.typeParams.map((name) => ({ k: 'param', name })) } : UNKNOWN;
  }

  /**
   * Runs `walk` over a body that returns `expected` (for target-typed `new()` in a `return`),
   * collecting the types of the `return` statements it meets.
   */
  private withReturns(expected: Ty, walk: () => void): Ty[] {
    const outer = this.returnSink;
    const outerExpected = this.expectedReturn;
    const sink: Ty[] = [];
    this.returnSink = sink;
    this.expectedReturn = expected;
    try {
      walk();
    } finally {
      this.returnSink = outer;
      this.expectedReturn = outerExpected;
    }
    return sink;
  }

  // ---------------------------------------------------------------- recording

  private lineOf(span: Span): number {
    return this.file.lines.at(span.start).line;
  }

  private edge(to: string, kind: CodeCall['kind'], certainty: CodeCall['certainty'], span: Span, at?: Expr): CodeCall {
    const call: CodeCall = { from: this.from, to, kind, certainty, file: this.file.path, line: this.lineOf(span) };
    if (this.endpoint) call.endpoint = this.endpoint;
    if (this.mocking || (at && checksReceived(at))) call.mock = true;
    if (at) this.edgeOf.set(at, call);
    this.calls.push(call);
    return call;
  }

  /** A call into the framework or a package that is given values of the code's types: where the code is left. */
  private handoff(recv: Ty | null, name: string, argTypes: Ty[], span: Span): void {
    if (this.mocking || configuresFake(name) || COMPARES.has(name)) return;
    const host = recv?.k === 'type' ? recv.ty : recv;
    if (host && keepsValues(host)) return;
    const types = [...new Set(argTypes.flatMap((t) => (t.k === 'code' && t.sym.kind !== 'enum' && t.sym.kind !== 'delegate' ? [t.sym.id] : [])))];
    if (!types.length) return;
    // As written: `ISender.Send`, `Results.Ok`, or the framework base of the class around (`ControllerBase.Ok`).
    let owner: string | null = null;
    if (host?.k === 'ext' && host.name !== '?') owner = host.name;
    else if (host?.k === 'code') owner = host.sym.name;
    else if (!host && this.owner) {
      const base = this.table.externalBases(this.self()).find((b) => b.k === 'ext');
      if (base?.k === 'ext') owner = base.name;
    }
    this.handoffs.push({ from: this.from, to: owner ? `${owner}.${name}` : name, types, file: this.file.path, line: this.lineOf(span), ...(this.endpoint && { endpoint: this.endpoint }) });
  }

  /** A URL given as an argument: written there, kept in a variable, or returned by a helper. */
  private urlOf(arg: Arg | undefined, ty: Ty | undefined, scope: Scope): string | null {
    const written = urlPattern(arg?.value);
    if (written !== null) return this.expandUrl(written, scope);
    const kept = ty ? this.urls.get(ty) : undefined;
    if (kept) return kept;
    // `Rute(o, "avviksgjennomgang/complete")`: the helper's URL, with its parameters filled in from the call.
    const e = arg?.value;
    if (e?.kind !== 'call' && e?.kind !== 'member') return null;
    const called = this.edgeOf.get(e);
    const member = called ? this.table.members.get(called.to) : undefined;
    const url = member ? this.returnedUrl(member) : null;
    if (!member || url === null) return null;
    const args = e.kind === 'call' ? e.args : [];
    const positional = args.filter((a) => a.name === null);
    const offset = member.isExtension && e.kind === 'call' && e.callee.kind === 'member' ? 1 : 0;
    return url.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (hole, name: string) => {
      const index = member.params.findIndex((p) => p.name === name);
      const given = args.find((a) => a.name === name) ?? (index >= offset ? positional[index - offset] : undefined);
      return (given && urlPattern(given.value)) ?? hole;
    });
  }

  /** The URL a helper returns, when its code is only that: `string Rute(Oppsett o, string tail) => $"/api/forbund/{o.ForbundId}/{tail}"`. */
  private returnedUrl(m: MemberSymbol): string | null {
    const known = this.returnedUrls.get(m);
    if (known !== undefined) return known;
    let value: Expr | undefined;
    if (m.decl.kind === 'method') {
      const body = m.decl.method.body ?? [];
      const only = body.length === 1 ? body[0] : undefined;
      value = only?.kind === 'expr' && only.arrow ? only.expr : only?.kind === 'return' ? (only.value ?? undefined) : undefined;
    } else if (m.decl.kind === 'property') value = m.decl.property.expressionBody ?? undefined;
    const url = urlPattern(value);
    this.returnedUrls.set(m, url);
    return url;
  }

  /** A URL with each hole that names a variable holding a known URL filled in: `{baseUrl}/vedlegg`. */
  private expandUrl(url: string, scope: Scope): string {
    return url.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (hole, name: string) => {
      const ty = scope.get(name);
      return (ty && this.urls.get(ty)) ?? hole;
    });
  }

  /** An HTTP request: an HttpClient's `PostAsJsonAsync(url, ...)` and the like, or `SendAsync(request)`. */
  private httpRequest(recv: Ty, name: string, args: Arg[], argTypes: Ty[], span: Span, scope: Scope): void {
    const client = recv.k === 'ext' && recv.name === 'HttpClient';
    const loose = recv.k === 'unknown' || (recv.k === 'ext' && (recv.name === '?' || recv.name === 'dynamic'));
    if (!client && !loose) return;
    let method = httpMethodOf(name);
    let url: string | null = null;
    if (method) url = this.urlOf(args[0], argTypes[0], scope);
    else if (name === 'SendAsync' || name === 'Send') {
      const message = argTypes[0] ? this.requestMessages.get(argTypes[0]) : undefined;
      if (message) ({ method, url } = message);
    }
    if (!method || url === null) return;
    // On a value of unknown type, only what is plainly a URL.
    if (!client && !/^(\/|https?:\/\/)/i.test(url)) return;
    this.requests.push({ from: this.from, method, url, endpoints: [], certainty: 'guess', file: this.file.path, line: this.lineOf(span), ...(this.endpoint && { endpoint: this.endpoint }) });
  }

  /** Walks code that only names members of a fake (the lambda of `mock.Setup(...)`), marking its calls. */
  private faking<T>(on: boolean, walk: () => T): T {
    if (!on || this.mocking) return walk();
    this.mocking = true;
    try {
      return walk();
    } finally {
      this.mocking = false;
    }
  }

  /** An EF Core entity reached through its DbSet. */
  private data(ty: Ty, span: Span): void {
    if (ty.k === 'ext' && ty.name === 'DbSet' && ty.args[0]?.k === 'code') this.use(ty.args[0].sym, 'data', span);
  }

  /** The prefix routes mapped on a builder get, and the method parameter it comes from. */
  private routeBase(ty: Ty): RouteBase {
    const group = this.groups.get(ty);
    if (group) return group;
    const from = this.builderParams.get(ty);
    return from ? { prefix: '', from } : { prefix: '' };
  }

  /** Route builders handed to a method (an extension method's receiver too). */
  private routeArgs(member: MemberSymbol, recv: Ty | null, argTypes: Ty[]): void {
    const given = member.isExtension && recv ? [recv, ...argTypes] : argTypes;
    given.forEach((ty, index) => {
      if (ty.k === 'ext' && ROUTE_BUILDERS.has(ty.name)) this.routeCalls.push({ ...this.routeBase(ty), to: { member: member.id, index } });
    });
  }

  /**
   * The minimal API endpoints, with the prefixes their methods' callers give: `app.MapGroup("/api")`
   * handed to `MapAuthEndpoints` puts `/api` in front of its routes. A method mapped through two
   * groups gives an endpoint for each. Calls and uses in a handler lambda keep the first one's id.
   */
  routedEndpoints(): CodeEndpoint[] {
    const prefixes = (p: RouteParam, depth: number): string[] => {
      const calls = this.routeCalls.filter((c) => c.to.member === p.member && c.to.index === p.index);
      // Nobody in the code hands it a builder: the routes as written.
      if (!calls.length || depth > 8) return [''];
      return [...new Set(calls.flatMap((c) => (c.from ? prefixes(c.from, depth + 1).map((x) => joinRoute(x, c.prefix)) : [joinRoute('', c.prefix)])))];
    };
    const taken = new Set<string>();
    const renamed = new Map<string, string>();
    const out: CodeEndpoint[] = [];
    for (const e of this.endpoints) {
      const via = this.endpointsVia.get(e);
      const routes = via ? prefixes(via, 0).map((p) => joinRoute(p, e.route)) : [e.route];
      let first: string | null = null;
      for (const route of routes) {
        const base = `${e.methods.join(',') || 'ANY'} ${route}`;
        let id = base;
        for (let n = 2; taken.has(id); n++) id = `${base}#${n}`;
        taken.add(id);
        out.push(first === null ? { ...e, id, route } : { ...e, id, route, sameHandler: first });
        first ??= id;
      }
      if (first !== e.id) renamed.set(e.id, first!);
    }
    // Handler lambdas' calls and uses name their endpoint.
    if (renamed.size) {
      for (const c of this.calls) if (c.endpoint && renamed.has(c.endpoint)) c.endpoint = renamed.get(c.endpoint)!;
      for (const u of this.uses.values()) if (u.endpoint && renamed.has(u.endpoint)) u.endpoint = renamed.get(u.endpoint)!;
      for (const h of this.handoffs) if (h.endpoint && renamed.has(h.endpoint)) h.endpoint = renamed.get(h.endpoint)!;
      for (const r of this.requests) if (r.endpoint && renamed.has(r.endpoint)) r.endpoint = renamed.get(r.endpoint)!;
    }
    this.endpointIds.clear();
    for (const id of taken) this.endpointIds.add(id);
    return out;
  }

  /** A minimal API endpoint: `app.MapGet("/orders/{id}", handler)`, inside any route groups. */
  private mapEndpoint(e: Extract<Expr, { kind: 'call' }>, recv: Ty, name: string, scope: Scope): boolean {
    const literal = routeLiteral(e.args[0]?.value);
    const methods = name === 'MapMethods' ? stringsIn(e.args[1]?.value) : MAP_METHODS[name];
    const handler = e.args.at(-1)?.value;
    if (literal === null || !methods || e.args.length < 2 || !handler) return false;
    const start = this.routeBase(recv);
    const route = joinRoute(start.prefix, literal);
    const base = `${methods.join(',') || 'ANY'} ${route}`;
    let id = base;
    for (let n = 2; this.endpointIds.has(id); n++) id = `${base}#${n}`;
    this.endpointIds.add(id);
    let handlerMember: string | null = null;
    if (handler.kind === 'name' || handler.kind === 'member') handlerMember = this.methodGroup(handler, scope)?.id ?? null;
    const endpoint: CodeEndpoint = { id, methods, route, kind: 'minimal', handler: handlerMember, mappedIn: this.from, file: this.file.path, line: this.lineOf(nameEnd(e.callee)) };
    this.endpoints.push(endpoint);
    if (start.from) this.endpointsVia.set(endpoint, start.from);
    for (const a of e.args.slice(1, -1)) this.expr(a.value, scope);
    if (handler.kind === 'lambda') {
      const outer = this.endpoint;
      this.endpoint = id;
      try {
        this.lambda(handler, scope, handler.params.map(() => OPAQUE));
      } finally {
        this.endpoint = outer;
      }
    }
    return true;
  }

  /** The method a method group names (`GetProduct`, `Handlers.Get`), without calling it. */
  private methodGroup(e: Expr, scope: Scope): MemberSymbol | null {
    const isMethod = (m: MemberSymbol) => m.kind === 'method';
    if (e.kind === 'name') return this.ownLevels(e.name, isMethod)[0]?.[0]?.member ?? null;
    if (e.kind !== 'member') return null;
    const target = this.expr(e.target, scope);
    const host = target.k === 'type' ? target.ty : target;
    return host.k === 'code' ? (this.levels(host, e.name, isMethod)[0]?.[0]?.member ?? null) : null;
  }

  private use(sym: TypeSymbol, kind: CodeUse['kind'], span: Span): void {
    if (sym === this.owner) return;
    const key = `${this.from}|${sym.id}|${kind}|${this.endpoint ?? ''}`;
    const existing = this.uses.get(key);
    if (existing) existing.count++;
    else this.uses.set(key, { from: this.from, to: sym.id, kind, file: this.file.path, line: this.lineOf(span), count: 1, ...(this.endpoint && { endpoint: this.endpoint }) });
  }

  /** Records the code types inside a type (type arguments, array elements) as plain uses. */
  private useInside(ty: Ty, span: Span, skip: Ty | null = null): void {
    switch (ty.k) {
      case 'code':
        if (ty !== skip) this.use(ty.sym, 'type', span);
        for (const a of ty.args) this.useInside(a, span);
        break;
      case 'ext':
        // `Mock<IOrderRepository>`: the test replaces that type with a fake.
        if (FAKE_TYPES.has(ty.name) && ty.args[0]?.k === 'code') this.use(ty.args[0].sym, 'mock', span);
        for (const a of ty.args) this.useInside(a, span);
        break;
      case 'array':
        this.useInside(ty.elem, span);
        break;
      case 'tuple':
        for (const t of ty.items) this.useInside(t, span);
        break;
    }
  }

  /** Resolves a written type and records the code types in it. */
  private useType(ref: TypeRef, kind: CodeUse['kind'] = 'type'): Ty {
    const ty = this.resolve(ref, false);
    if (ty.k === 'code' && kind !== 'type') {
      this.use(ty.sym, kind, ref.span);
      this.useInside(ty, ref.span, ty);
    } else this.useInside(ty, ref.span);
    return ty;
  }

  private resolve(ref: TypeRef, record: boolean): Ty {
    const ty = this.table.resolve(ref, this.where);
    if (record) this.useInside(ty, ref.span);
    return ty;
  }

  private attributes(list: CsAttribute[]): void {
    for (const a of list) {
      const name = a.name.slice(a.name.lastIndexOf('.') + 1);
      for (const candidate of name.endsWith('Attribute') ? [name] : [`${name}Attribute`, name]) {
        const found = this.table.lookupName(candidate, 0, this.where);
        if (found instanceof TypeSymbol) {
          this.use(found, 'attribute', a.span);
          break;
        }
      }
    }
  }

  // ---------------------------------------------------------------- members of types

  private valueTypeOf(m: MemberSymbol): Ty {
    let ty = this.valueTypes.get(m);
    if (!ty) {
      if (m.kind === 'enumMember' || m.kind === 'constructor') ty = { k: 'code', sym: m.owner, args: [] };
      else ty = m.valueType ? this.table.resolve(m.valueType, { ctx: m.ctx, type: m.owner, typeParams: m.typeParams }) : UNKNOWN;
      this.valueTypes.set(m, ty);
    }
    return ty;
  }

  private paramTypesOf(m: MemberSymbol): Ty[] {
    let list = this.paramTypes.get(m);
    if (!list) {
      const where: Where = { ctx: m.ctx, type: m.owner, typeParams: m.typeParams };
      list = m.params.map((p) => (p.type ? this.table.resolve(p.type, where) : UNKNOWN));
      this.paramTypes.set(m, list);
    }
    return list;
  }

  private ctorsOf(sym: TypeSymbol, subst: Subst): Candidate[] {
    return (sym.byName.get('.ctor') ?? []).filter((m) => !m.isStatic).map((member) => ({ member, subst }));
  }

  /** Members called `name` on a type and its bases, one list per level, most derived first. */
  private levels(ty: Ty, name: string, filter: (m: MemberSymbol) => boolean): Candidate[][] {
    const out: Candidate[][] = [];
    for (const { sym, subst } of this.table.hierarchy(ty)) {
      const list = (sym.byName.get(name) ?? []).filter(filter).map((member) => ({ member, subst }));
      if (list.length) out.push(list);
    }
    return out;
  }

  /** Members called `name` seen from inside the current type: its hierarchy, then the types it is nested in. */
  private ownLevels(name: string, filter: (m: MemberSymbol) => boolean): Candidate[][] {
    const out: Candidate[][] = [];
    for (let t = this.owner; t; t = t.outer) {
      const self: Ty = { k: 'code', sym: t, args: t.typeParams.map((p) => ({ k: 'param', name: p })) };
      out.push(...this.levels(self, name, filter));
    }
    for (const s of this.where.ctx.staticUsings) {
      const found = this.table.lookupName(s.slice(s.lastIndexOf('.') + 1), 0, this.where);
      const host = found instanceof TypeSymbol ? found : this.findQualifiedType(s);
      if (host) out.push(...this.levels({ k: 'code', sym: host, args: [] }, name, (m) => m.isStatic && filter(m)));
    }
    return out;
  }

  private findQualifiedType(name: string): TypeSymbol | null {
    const ty = this.table.resolve({ name, args: [], nullable: false, arrays: [], span: { start: 0, end: 0 } }, this.where);
    return ty.k === 'code' ? ty.sym : null;
  }

  private applicable(m: MemberSymbol, argc: number): boolean {
    const params = m.params;
    const variadic = params.some((p) => p.modifiers?.includes('params'));
    const required = params.filter((p) => !p.optional && !p.modifiers?.includes('params')).length;
    return argc >= required && (variadic || argc <= params.length);
  }

  /** Overload resolution, roughly: the most derived level with an applicable member, then the best match of argument types. */
  private pick(levels: Candidate[][], argc: number, argTypes: Ty[], extension: boolean): Picked | null {
    const offset = extension ? 1 : 0;
    for (const level of levels) {
      const ok = level.filter((c) => this.applicable(c.member, argc + offset));
      if (ok.length === 0) continue;
      if (ok.length === 1) return { ...ok[0]!, ambiguous: false };
      const scored = ok.map((c) => {
        const params = this.paramTypesOf(c.member).slice(offset);
        let score = 0;
        argTypes.forEach((arg, i) => {
          const p = params[i];
          if (!p) return;
          const s = matchScore(arg, substitute(p, c.subst));
          if (s < 0) score -= 100;
          else score += s;
        });
        // Fewer parameters left to defaults is closer.
        score -= (params.length - argc) * 0.01;
        return { c, score };
      });
      scored.sort((a, b) => b.score - a.score);
      const best = scored[0]!;
      const tied = scored.filter((s) => s.score === best.score && s.c.member !== best.c.member);
      return { ...best.c, ambiguous: tied.length > 0 };
    }
    return null;
  }

  /** The type a member gives on a receiver with `subst`, and explicit or inferred method type arguments. */
  private resultOf(m: MemberSymbol, subst: Subst, typeArgs: Ty[], argTypes: Ty[]): Ty {
    let s = subst;
    if (m.typeParams.length) {
      s = new Map(subst);
      if (typeArgs.length === m.typeParams.length) m.typeParams.forEach((p, i) => s.set(p, typeArgs[i]!));
      else {
        const params = this.paramTypesOf(m).slice(m.isExtension ? 1 : 0);
        params.forEach((p, i) => {
          const arg = argTypes[i];
          if (p.k === 'param' && m.typeParams.includes(p.name) && arg && arg.k !== 'unknown' && !s.has(p.name)) s.set(p.name, arg);
        });
      }
    }
    return substitute(this.valueTypeOf(m), s);
  }

  /** Extension methods named `name` that apply to `recv`, best match first. */
  private extensionsFor(recv: Ty, name: string, argc: number): Picked | null {
    const list = (this.table.extensions.get(name) ?? []).filter((m) => this.isVisible(m) && this.applicable(m, argc + 1));
    if (!list.length) return null;
    const scored = list
      .map((member) => {
        const thisTy = this.paramTypesOf(member)[0] ?? UNKNOWN;
        return { member, score: this.receiverScore(recv, thisTy, member) };
      })
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score);
    if (!scored.length) return null;
    const best = scored[0]!;
    const subst: Subst = new Map();
    const thisTy = this.paramTypesOf(best.member)[0];
    if (thisTy?.k === 'param') subst.set(thisTy.name, recv);
    else if (thisTy && (thisTy.k === 'code' || thisTy.k === 'ext') && (recv.k === 'code' || recv.k === 'ext')) {
      // `this IEnumerable<T> items` called on a List<Order>: T is Order.
      const elemThis = elementType(thisTy);
      const elemRecv = elementType(recv, (t) => this.table.externalBases(t));
      if (elemThis?.k === 'param' && elemRecv) subst.set(elemThis.name, elemRecv);
      thisTy.args.forEach((a, i) => {
        if (a.k === 'param' && recv.args[i] && !subst.has(a.name)) subst.set(a.name, recv.args[i]!);
      });
    }
    return { member: best.member, subst, ambiguous: scored.filter((s) => s.score === best.score).length > 1 };
  }

  private isVisible(m: MemberSymbol): boolean {
    let t = m.owner;
    while (t.outer) t = t.outer;
    return this.visible.has(t.namespace ?? '');
  }

  /** How well a receiver fits an extension method's `this` parameter; 0 is not at all. */
  private receiverScore(recv: Ty, thisTy: Ty, m: MemberSymbol): number {
    if (thisTy.k === 'param') return m.typeParams.includes(thisTy.name) ? 1 : 0;
    if (thisTy.k === 'code') {
      if (recv.k !== 'code') return 0;
      if (recv.sym === thisTy.sym) return 4;
      return this.table.isAssignable(recv.sym, thisTy.sym) ? 3 : 0;
    }
    if (thisTy.k === 'ext') {
      if (recv.k === 'ext') {
        if (recv.name === thisTy.name) return 4;
        if (implementsInterface(recv.name, thisTy.name)) return 3;
        if (elementType(thisTy) && elementType(recv)) return 2;
        return 0;
      }
      if (recv.k === 'code') return this.table.externalBases(recv).some((e) => e.k === 'ext' && e.name === thisTy.name) ? 3 : 0;
      if (recv.k === 'array') return elementType(thisTy) ? 2 : 0;
    }
    return 0;
  }

  /** The only method of the code called `name` that takes `argc` arguments, for a call whose receiver is unknown. */
  private guess(name: string, argc: number): MemberSymbol | null {
    const found = new Set<MemberSymbol>();
    for (const m of this.table.methodsByName.get(name) ?? []) {
      if (m.isExtension ? this.applicable(m, argc + 1) || this.applicable(m, argc) : this.applicable(m, argc)) found.add(m);
    }
    return found.size === 1 ? [...found][0]! : null;
  }

  // ---------------------------------------------------------------- statements

  private stmts(list: Stmt[], scope: Scope): void {
    for (const s of list) this.stmt(s, scope);
  }

  private stmt(s: Stmt, scope: Scope): void {
    switch (s.kind) {
      case 'expr':
        if (s.arrow) this.returnSink?.push(this.expr(s.expr, scope, this.expectedReturn));
        else this.expr(s.expr, scope);
        return;
      case 'local': {
        if (s.deconstruct) {
          // Each name takes its own type, or its part of the value's tuple type.
          const init = s.declarators[0]?.init;
          const value = init ? this.expr(init, scope) : UNKNOWN;
          const parts = value.k === 'tuple' && value.items.length === s.declarators.length ? value.items : null;
          s.declarators.forEach((d, i) => scope.set(d.name, d.type ? this.useType(d.type) : (parts?.[i] ?? UNKNOWN)));
          return;
        }
        const declared = s.declType ? this.useType(s.declType) : null;
        for (const d of s.declarators) {
          let ty = d.init ? this.expr(d.init, scope, declared ?? UNKNOWN) : UNKNOWN;
          // `var url = $"/orders/{id}"`: a URL to send a request to later.
          const written = urlPattern(d.init ?? undefined);
          const url = written === null ? null : this.expandUrl(written, scope);
          if (url !== null && url.includes('/')) {
            ty = { ...(ty.k === 'unknown' ? ext('string') : ty) };
            this.urls.set(ty, url);
          }
          // `RouteGroupBuilder api = app.MapGroup("/api")` keeps the group's prefix, a request message its request.
          scope.set(d.name, declared && !this.groups.has(ty) && !this.urls.has(ty) && !this.requestMessages.has(ty) ? declared : ty);
        }
        return;
      }
      case 'block':
        this.stmts(s.body, scope.child());
        return;
      case 'if':
        this.expr(s.test, scope);
        this.stmt(s.then, scope.child());
        if (s.else) this.stmt(s.else, scope.child());
        return;
      case 'loop': {
        const inner = scope.child();
        for (const init of s.init ?? []) this.stmt(init, inner);
        if (s.collection) {
          const coll = this.expr(s.collection, inner);
          const elem = s.variable?.type ? this.useType(s.variable.type) : (elementType(coll, (t) => this.table.externalBases(t)) ?? UNKNOWN);
          if (s.variable?.name) inner.set(s.variable.name, elem);
          if (s.variable?.names) {
            const parts = elem.k === 'tuple' ? elem.items : elem.k === 'ext' && elem.name === 'KeyValuePair' ? elem.args : [];
            const types = s.variable.types;
            s.variable.names.forEach((n, i) => inner.set(n, types?.[i] ? this.useType(types[i]!) : (parts[i] ?? UNKNOWN)));
          }
        }
        if (s.condition) this.expr(s.condition, inner);
        for (const u of s.update ?? []) this.expr(u, inner);
        this.stmt(s.body, inner);
        return;
      }
      case 'return': {
        const ty = s.value ? this.expr(s.value, scope, this.expectedReturn) : UNKNOWN;
        this.returnSink?.push(ty);
        return;
      }
      case 'throw':
      case 'yield':
        if (s.value) this.expr(s.value, scope);
        return;
      case 'try':
        this.stmts(s.block.body, scope.child());
        for (const c of s.catches) {
          const inner = scope.child();
          const ty = c.type ? this.useType(c.type) : UNKNOWN;
          if (c.name) inner.set(c.name, ty);
          if (c.filter) this.expr(c.filter, inner);
          this.stmts(c.block.body, inner);
        }
        if (s.finally) this.stmts(s.finally.body, scope.child());
        return;
      case 'switch': {
        const subject = this.expr(s.subject, scope);
        for (const section of s.sections) {
          const inner = scope.child();
          for (const label of section.labels) {
            if (label.kind !== 'case') continue;
            this.pattern(label.pattern, inner, subject);
            if (label.guard) this.expr(label.guard, inner);
          }
          this.stmts(section.body, inner);
        }
        return;
      }
      case 'guarded': {
        const inner = scope.child();
        if (s.head?.kind === 'local') this.stmt(s.head, inner);
        else if (s.head) this.expr(s.head, inner);
        this.stmt(s.body, inner);
        return;
      }
      case 'localFunction': {
        const method = s.method;
        const outerWhere = this.where;
        const outerConstraints = this.constraints;
        this.where = { ...outerWhere, typeParams: [...outerWhere.typeParams, ...method.typeParams] };
        if (method.constraints) this.constraints = new Map([...outerConstraints, ...method.constraints.map((c) => [c.param, c.types] as const)]);
        try {
          const returnTy = method.returnType ? this.useType(method.returnType) : UNKNOWN;
          scope.setFn(method.name, returnTy);
          const inner = scope.child();
          for (const p of method.params) inner.set(p.name, p.type ? this.useType(p.type) : UNKNOWN);
          if (method.body) this.withReturns(method.modifiers.includes('async') ? awaited(returnTy) : returnTy, () => this.stmts(method.body!, inner));
        } finally {
          this.where = outerWhere;
          this.constraints = outerConstraints;
        }
        return;
      }
      case 'unknown':
        this.scan(s.span);
        return;
      case 'empty':
        return;
    }
  }

  /** A pattern: records its type and declares its variable. */
  private pattern(p: Pattern, scope: Scope, subject: Ty): void {
    const ty = p.type ? this.typeOrConstant(p.type) : null;
    if (p.designation) scope.set(p.designation, ty ?? subject);
    if (p.value) this.expr(p.value, scope);
  }

  /** `case Status.Open:` reads like a type; when it names an enum member it is a constant. */
  private typeOrConstant(ref: TypeRef): Ty {
    const ty = this.resolve(ref, false);
    if (ty.k === 'code') {
      this.use(ty.sym, 'type', ref.span);
      return ty;
    }
    const dot = ref.name.lastIndexOf('.');
    if (dot > 0 && !ref.args.length) {
      const host = this.table.resolve({ ...ref, name: ref.name.slice(0, dot) }, this.where);
      if (host.k === 'code') {
        const member = host.sym.byName.get(ref.name.slice(dot + 1))?.[0];
        if (member) {
          this.edge(member.id, 'access', 'exact', ref.span);
          return { k: 'code', sym: host.sym, args: [] };
        }
      }
    }
    return ty;
  }

  /** Code the parser could not read: `Name(` and `.Name(` become guesses by name. */
  private scan(span: Span): void {
    const text = this.file.text.slice(span.start, span.end);
    const tokens = lex(text);
    for (let i = 0; i + 1 < tokens.length; i++) {
      const t = tokens[i]!;
      const next = tokens[i + 1]!;
      if (t.kind !== 'ident' || next.kind !== 'punct' || next.value !== '(' || NOT_CALLS.has(t.value)) continue;
      const prev = tokens[i - 1];
      const at = { start: span.start + t.start, end: span.start + t.end };
      if (prev?.kind === 'ident' && prev.value === 'new') {
        const found = this.table.lookupName(t.value, 0, this.where);
        if (found instanceof TypeSymbol) this.edge(found.id, 'new', 'guess', at);
        continue;
      }
      const own = prev?.kind === 'punct' && prev.value === '.' ? [] : this.ownLevels(t.value, (m) => m.kind === 'method');
      const target = own[0]?.length === 1 ? own[0][0]!.member : this.guess(t.value, countArgs(tokens, i + 1));
      if (target) this.edge(target.id, 'call', 'guess', at);
    }
  }

  // ---------------------------------------------------------------- expressions

  private expr(e: Expr, scope: Scope, expected: Ty = UNKNOWN): Ty {
    switch (e.kind) {
      case 'literal':
        for (const h of e.holes ?? []) this.expr(h, scope);
        switch (e.literal) {
          case 'string':
            return ext('string');
          case 'bool':
            return ext('bool');
          case 'char':
            return ext('char');
          case 'number':
            return ext(e.suffix === 'm' ? 'decimal' : e.suffix === 'l' ? 'long' : e.suffix === 'f' ? 'float' : e.suffix === 'd' || e.raw.includes('.') ? 'double' : 'int');
          default:
            return UNKNOWN;
        }
      case 'name':
        return this.name(e, scope);
      case 'member':
        return this.member(e, scope);
      case 'call':
        return this.call(e, scope);
      case 'index':
        return this.index(e, scope);
      case 'new':
        return this.newObject(e, scope, expected);
      case 'anonymous':
        for (const m of e.members) this.expr(m.value, scope);
        return UNKNOWN;
      case 'array': {
        const elem = e.elementType ? this.useType(e.elementType, 'new') : expected.k === 'array' ? expected.elem : UNKNOWN;
        for (const size of e.sizes) this.expr(size, scope);
        let first: Ty = UNKNOWN;
        for (const item of e.init?.items ?? []) {
          const t = this.expr(item, scope, elem);
          if (first.k === 'unknown') first = t;
        }
        return { k: 'array', elem: elem.k === 'unknown' ? first : elem };
      }
      case 'initializer': {
        const elem = elementType(expected, (t) => this.table.externalBases(t)) ?? UNKNOWN;
        for (const item of e.items) this.expr(item, scope, elem);
        return expected;
      }
      case 'indexInit':
        for (const a of e.args) this.expr(a.value, scope);
        this.expr(e.value, scope);
        return UNKNOWN;
      case 'lambda':
        return this.lambda(e, scope, delegateParams(expected) ?? this.codeDelegateParams(expected));
      case 'typeof':
        this.useType(e.type);
        return ext('Type');
      case 'default':
        return e.type ? this.useType(e.type) : expected;
      case 'cast': {
        const ty = this.useType(e.type);
        this.expr(e.expr, scope);
        return ty;
      }
      case 'unary': {
        const operand = this.expr(e.operand, scope);
        if (e.op === 'await') return awaited(operand);
        if (e.op === 'throw') return UNKNOWN;
        if (e.op === '!' && !e.postfix) return ext('bool');
        return operand;
      }
      case 'binary': {
        const left = this.expr(e.left, scope);
        const right = this.expr(e.right, scope, left);
        if (e.op === '??') return left.k !== 'unknown' ? left : right;
        if (COMPARISON.has(e.op)) return ext('bool');
        if (e.op === '+' && ((left.k === 'ext' && left.name === 'string') || (right.k === 'ext' && right.name === 'string'))) return ext('string');
        return left.k !== 'unknown' ? left : right;
      }
      case 'isas': {
        const subject = this.expr(e.expr, scope);
        const ty = e.type ? this.typeOrConstant(e.type) : null;
        if (e.designation) scope.set(e.designation, ty ?? subject);
        return e.op === 'as' ? (ty ?? UNKNOWN) : ext('bool');
      }
      case 'conditional': {
        this.expr(e.test, scope);
        const a = this.expr(e.whenTrue, scope, expected);
        const b = this.expr(e.whenFalse, scope, expected.k !== 'unknown' ? expected : a);
        return a.k !== 'unknown' ? a : b;
      }
      case 'assign': {
        const target = this.expr(e.target, scope);
        this.expr(e.value, scope, target);
        return target;
      }
      case 'tuple':
        return { k: 'tuple', items: e.items.map((i) => this.expr(i.value, scope)) };
      case 'switch': {
        const subject = this.expr(e.subject, scope);
        let result: Ty = UNKNOWN;
        for (const arm of e.arms) {
          const inner = scope.child();
          this.pattern(arm.pattern, inner, subject);
          if (arm.guard) this.expr(arm.guard, inner);
          const t = this.expr(arm.value, inner, expected);
          if (result.k === 'unknown') result = t;
        }
        return result;
      }
      case 'with': {
        const target = this.expr(e.target, scope);
        this.objectInitializer(e.init.items, target, scope);
        return target;
      }
      case 'query':
        return this.query(e, scope);
      case 'unknown':
        this.scan(e.span);
        return UNKNOWN;
    }
  }

  private name(e: Extract<Expr, { kind: 'name' }>, scope: Scope): Ty {
    const n = e.name;
    if (n === 'this') return this.self();
    if (n === 'base') return (this.owner && this.table.basesOf(this.owner).base) ?? OPAQUE;
    const local = scope.get(n);
    if (local) return local;
    // `string.Join(...)`, `int.Parse(...)`
    if (PREDEFINED_TYPES.has(n)) return { k: 'type', ty: ext(n) };
    const own = this.ownLevels(n, (m) => m.kind !== 'method' && m.kind !== 'constructor' && m.kind !== 'destructor' && m.kind !== 'operator' && m.kind !== 'conversion' && m.kind !== 'indexer');
    if (own.length) {
      const { member, subst } = own[0]![0]!;
      this.edge(member.id, 'access', 'exact', e.span);
      const ty = substitute(this.valueTypeOf(member), subst);
      this.data(ty, e.span);
      return ty;
    }
    // A type, for static access.
    const typeArgs = (e.typeArgs ?? []).map((t) => this.useType(t));
    const found = this.table.lookupName(n, typeArgs.length, this.where);
    if (found instanceof TypeSymbol) {
      this.use(found, 'static', e.span);
      return { k: 'type', ty: { k: 'code', sym: found, args: typeArgs } };
    }
    if (found) return { k: 'type', ty: { k: 'param', name: found.param } };
    if (this.table.namespaceNames.has(n)) return { k: 'ns', name: n };
    const alias = this.where.ctx.aliases.get(n);
    if (alias) return { k: 'type', ty: this.table.resolve(alias, this.where) };
    // Not declared anywhere in the code: a framework type (Console, Task, Math) or an inherited member.
    if (n[0]! >= 'A' && n[0]! <= 'Z') return { k: 'type', ty: ext(n, typeArgs) };
    return UNKNOWN;
  }

  private member(e: Extract<Expr, { kind: 'member' }>, scope: Scope): Ty {
    const target = this.constrained(this.expr(e.target, scope), e.name);
    const typeArgs = (e.typeArgs ?? []).map((t) => this.useType(t));
    switch (target.k) {
      case 'ns': {
        const full = `${target.name}.${e.name}`;
        const ty = this.table.resolve({ name: full, args: [], nullable: false, arrays: [], span: e.span }, { ...this.where, ctx: { ...this.where.ctx, usings: [] } });
        if (ty.k === 'code') {
          this.use(ty.sym, 'static', e.span);
          return { k: 'type', ty: typeArgs.length ? { ...ty, args: typeArgs } : ty };
        }
        if (this.table.namespaceNames.has(full)) return { k: 'ns', name: full };
        return { k: 'type', ty: ext(e.name, typeArgs) };
      }
      case 'type': {
        const host = target.ty;
        // A framework namespace or type: `System.IO.File`, `DateTime.Now`.
        if (host.k !== 'code') return isUpper(e.name) ? { k: 'type', ty: ext(e.name, typeArgs) } : OPAQUE;
        const nested = host.sym.nested.get(e.name)?.find((t) => t.arity === typeArgs.length);
        if (nested) {
          this.use(nested, 'static', e.span);
          return { k: 'type', ty: { k: 'code', sym: nested, args: typeArgs } };
        }
        const found = this.levels(host, e.name, isValueMember)[0]?.[0];
        if (found) {
          this.edge(found.member.id, 'access', 'exact', e.span);
          return substitute(this.valueTypeOf(found.member), found.subst);
        }
        return UNKNOWN;
      }
      case 'code': {
        const found = this.levels(target, e.name, isValueMember)[0]?.[0];
        if (found) {
          this.edge(found.member.id, 'access', 'exact', e.span, e);
          const ty = substitute(this.valueTypeOf(found.member), found.subst);
          this.data(ty, e.span);
          return ty;
        }
        return externalMember(target, e.name) ?? this.externalBaseMember(target, e.name) ?? (this.table.hasExternalBase(target) ? OPAQUE : UNKNOWN);
      }
      case 'ext':
      case 'array':
      case 'tuple':
        return externalMember(target, e.name) ?? OPAQUE;
      default:
        return UNKNOWN;
    }
  }

  /** A property of a framework base type: `Count` of a class deriving from `List<T>`. */
  private externalBaseMember(ty: Ty, name: string): Ty | null {
    for (const base of this.table.externalBases(ty)) {
      const t = externalMember(base, name);
      if (t) return t;
    }
    return null;
  }

  private index(e: Extract<Expr, { kind: 'index' }>, scope: Scope): Ty {
    const target = this.constrained(this.expr(e.target, scope), 'this[]');
    for (const a of e.args) this.expr(a.value, scope);
    if (target.k === 'code') {
      const indexer = this.levels(target, 'this[]', () => true)[0]?.[0];
      if (indexer) {
        this.edge(indexer.member.id, 'access', 'exact', e.span);
        return substitute(this.valueTypeOf(indexer.member), indexer.subst);
      }
      for (const base of this.table.externalBases(target)) {
        const t = indexType(base);
        if (t) return t;
      }
      return this.table.hasExternalBase(target) ? OPAQUE : UNKNOWN;
    }
    return indexType(target) ?? (target.k === 'unknown' || target.k === 'param' ? UNKNOWN : OPAQUE);
  }

  private newObject(e: Extract<Expr, { kind: 'new' }>, scope: Scope, expected: Ty): Ty {
    let ty = e.type ? this.useType(e.type, 'new') : expected;
    const argTypes = e.args.map((a) => (a.value.kind === 'lambda' ? UNKNOWN : this.argType(a, scope)));
    // new HttpRequestMessage(HttpMethod.Post, url): a request, sent later with SendAsync.
    if (ty.k === 'ext' && ty.name === 'HttpRequestMessage') {
      const method = httpMethodName(e.args[0]?.value);
      const url = this.urlOf(e.args[1], argTypes[1], scope);
      if (method && url !== null) {
        ty = { ...ty };
        this.requestMessages.set(ty, { method, url });
      }
    }
    let picked: Picked | null = null;
    if (ty.k === 'code') {
      const subst: Subst = new Map(ty.sym.typeParams.map((p, i) => [p, ty.args[i] ?? { k: 'param', name: p }]));
      const ctors = this.ctorsOf(ty.sym, subst);
      picked = ctors.length ? this.pick([ctors], argTypes.length, argTypes, false) : null;
      if (picked) this.edge(picked.member.id, 'new', picked.ambiguous ? 'guess' : 'exact', e.span);
      else this.edge(ty.sym.id, 'new', 'exact', e.span);
    } else if (ty.k === 'ext') this.external++;
    this.walkLambdaArgs(e.args, scope, picked);
    if (e.init) this.objectInitializer(e.init.items, ty, scope);
    return ty;
  }

  /** `{ Name = x, Lines = { a, b } }` on a new or `with` object: each name is a member of its type. */
  private objectInitializer(items: Expr[], ty: Ty, scope: Scope): void {
    const elem = elementType(ty, (t) => this.table.externalBases(t)) ?? UNKNOWN;
    for (const item of items) {
      if (item.kind === 'assign' && item.target.kind === 'name') {
        const found = ty.k === 'code' ? this.levels(ty, item.target.name, isValueMember)[0]?.[0] : undefined;
        let valueTy: Ty = UNKNOWN;
        if (found) {
          this.edge(found.member.id, 'access', 'exact', item.target.span);
          valueTy = substitute(this.valueTypeOf(found.member), found.subst);
        }
        if (item.value.kind === 'initializer') this.objectInitializer(item.value.items, valueTy, scope);
        else this.expr(item.value, scope, valueTy);
      } else this.expr(item, scope, elem);
    }
  }

  private argType(a: Arg, scope: Scope): Ty {
    if (a.declares !== undefined && a.value.kind === 'name') {
      const ty = a.declares ? this.useType(a.declares) : UNKNOWN;
      scope.set(a.value.name, ty);
      return ty;
    }
    return this.expr(a.value, scope);
  }

  private codeDelegateParams(ty: Ty): Ty[] | null {
    if (ty.k !== 'code' || ty.sym.kind !== 'delegate') return null;
    const part = ty.sym.parts[0]!;
    const subst: Subst = new Map(ty.sym.typeParams.map((p, i) => [p, ty.args[i] ?? { k: 'param', name: p }]));
    return (part.cls.primaryParams ?? []).map((p) => (p.type ? substitute(this.table.resolve(p.type, { ctx: part.ctx, type: ty.sym, typeParams: ty.sym.typeParams }), subst) : UNKNOWN));
  }

  /** Walks a lambda with its parameters typed, and returns the type its body gives (`result`: what it should return). */
  private lambda(e: LambdaExpr, scope: Scope, params: Ty[] | null, result: Ty = UNKNOWN): Ty {
    const inner = scope.child();
    e.params.forEach((p, i) => inner.set(p.name, p.type ? this.useType(p.type) : (params?.[i] ?? UNKNOWN)));
    if (e.body.kind === 'block') {
      const body = e.body.body;
      const returns = this.withReturns(result, () => this.stmts(body, inner));
      return returns.find((t) => t.k !== 'unknown') ?? UNKNOWN;
    }
    return this.expr(e.body, inner, result);
  }

  /** Walks the lambda arguments of a call, typed by the parameters of the member it resolved to. */
  private walkLambdaArgs(args: Arg[], scope: Scope, picked: Candidate | null): void {
    const offset = picked?.member.isExtension ? 1 : 0;
    args.forEach((a, i) => {
      if (a.value.kind !== 'lambda') return;
      const p = picked ? this.paramTypesOf(picked.member)[i + offset] : undefined;
      const pty = p ? substitute(p, picked!.subst) : UNKNOWN;
      this.lambda(a.value, scope, delegateParams(pty) ?? this.codeDelegateParams(pty), delegateResult(pty) ?? UNKNOWN);
    });
  }

  private call(e: Extract<Expr, { kind: 'call' }>, scope: Scope): Ty {
    const callee = e.callee;
    if (callee.kind !== 'member' && callee.kind !== 'name') {
      const fn = this.expr(callee, scope);
      for (const a of e.args) this.expr(a.value, scope);
      return delegateResult(fn) ?? UNKNOWN;
    }
    const name = callee.name;
    const typeArgs = (callee.typeArgs ?? []).map((t) => this.useType(t));
    const argc = e.args.length;

    if (callee.kind === 'name') {
      if (name === 'nameof') return ext('string');
      if (name === 'var') {
        for (const a of e.args) this.expr(a.value, scope);
        return UNKNOWN;
      }
      const argTypes = e.args.map((a) => (a.value.kind === 'lambda' ? UNKNOWN : this.argType(a, scope)));
      const local = scope.get(name);
      if (local) {
        this.walkLambdaArgs(e.args, scope, null);
        return delegateResult(local) ?? (local.k === 'code' && local.sym.kind === 'delegate' ? this.delegateReturn(local) : UNKNOWN);
      }
      const fn = scope.fn(name);
      if (fn) {
        this.walkLambdaArgs(e.args, scope, null);
        return fn;
      }
      const picked = this.pick(this.ownLevels(name, (m) => m.kind === 'method'), argc, argTypes, false);
      if (picked) {
        this.edge(picked.member.id, 'call', picked.ambiguous ? 'guess' : 'exact', nameEnd(callee), e);
        this.routeArgs(picked.member, null, argTypes);
        this.walkLambdaArgs(e.args, scope, picked);
        return this.resultOf(picked.member, picked.subst, typeArgs, argTypes);
      }
      this.walkLambdaArgs(e.args, scope, null);
      if (this.owner && this.table.hasExternalBase(this.self())) {
        this.external++;
        this.handoff(null, name, argTypes, nameEnd(callee));
      } else this.unresolved++;
      // `Set<Order>()` inside a DbContext.
      if (name === 'Set' && typeArgs.length === 1) {
        this.data(ext('DbSet', typeArgs), callee.span);
        return ext('DbSet', typeArgs);
      }
      return UNKNOWN;
    }

    const recv = this.constrained(this.expr(callee.target, scope), name);
    const argTypes = e.args.map((a) => (a.value.kind === 'lambda' ? UNKNOWN : this.argType(a, scope)));
    this.httpRequest(recv, name, e.args, argTypes, nameEnd(callee), scope);
    let picked: Picked | null = null;
    if (recv.k === 'code' || (recv.k === 'type' && recv.ty.k === 'code')) {
      const host = recv.k === 'type' ? recv.ty : recv;
      picked = this.pick(this.levels(host, name, (m) => m.kind === 'method'), argc, argTypes, false);
    }
    if (!picked && recv.k !== 'type' && recv.k !== 'ns' && recv.k !== 'unknown') picked = this.extensionsFor(recv, name, argc);
    // A framework value the table cannot type: the code's only extension method of that name for a framework type is a fair guess.
    if (!picked && recv.k === 'ext' && recv.name === '?') {
      const only = (this.table.extensions.get(name) ?? []).filter((m) => this.isVisible(m) && this.applicable(m, argc + 1) && this.paramTypesOf(m)[0]?.k === 'ext');
      if (only.length === 1) picked = { member: only[0]!, subst: new Map(), ambiguous: true };
    }
    if (picked) {
      this.edge(picked.member.id, 'call', picked.ambiguous ? 'guess' : 'exact', nameEnd(callee), e);
      this.routeArgs(picked.member, recv, argTypes);
      this.walkLambdaArgs(e.args, scope, picked);
      return this.resultOf(picked.member, picked.subst, typeArgs, argTypes);
    }
    // A receiver of a type parameter (its constraints are not read) is as good as unknown.
    if (recv.k === 'unknown' || recv.k === 'param') {
      const target = this.guess(name, argc);
      if (target) {
        this.edge(target.id, 'call', 'guess', nameEnd(callee), e);
        this.walkLambdaArgs(e.args, scope, { member: target, subst: new Map() });
        return UNKNOWN;
      }
      this.walkLambdaArgs(e.args, scope, null);
      this.unresolved++;
      return UNKNOWN;
    }
    // Minimal APIs: route groups carry their prefix; endpoints are recorded with their handlers.
    if (recv.k === 'ext' && name === 'MapGroup') {
      const literal = routeLiteral(e.args[0]?.value);
      if (literal !== null) {
        const group = ext('RouteGroupBuilder');
        const base = this.routeBase(recv);
        this.groups.set(group, { prefix: joinRoute(base.prefix, literal), from: base.from });
        this.external++;
        return group;
      }
    }
    if (recv.k === 'ext' && name in MAP_METHODS && this.mapEndpoint(e, recv, name, scope)) {
      this.external++;
      return OPAQUE;
    }
    if (name === 'Set' && typeArgs.length === 1) this.data(ext('DbSet', typeArgs), callee.span);
    // Mocking libraries: `Substitute.For<IRepo>()` fakes a type; `repo.FindAsync(1).Returns(order)` configures the call before it.
    for (const t of fakedBy(recv, name, typeArgs)) if (t.k === 'code') this.use(t.sym, 'mock', callee.span);
    if (configuresFake(name)) {
      const configured = this.edgeOf.get(callee.target);
      if (configured) configured.mock = true;
    }
    const faking = fakingLambda(recv, name);
    // The framework or a package: no line, but LINQ and friends still give types.
    const externals = (t: Ty) => this.table.externalBases(t);
    const done = new Set<number>();
    const lambdaAt = (index: number): Arg | undefined => e.args.filter((a) => a.value.kind === 'lambda')[index];
    const result = externalCall(recv, name, {
      typeArgs,
      argTypes,
      argCount: argc,
      externals,
      lambdaBody: (index, params) => {
        const arg = lambdaAt(index);
        if (!arg || arg.value.kind !== 'lambda') return UNKNOWN;
        done.add(e.args.indexOf(arg));
        return this.lambda(arg.value, scope, params);
      },
    });
    let lambdaIndex = 0;
    e.args.forEach((a, i) => {
      if (a.value.kind !== 'lambda') return;
      const index = lambdaIndex++;
      if (done.has(i)) return;
      done.add(i);
      // Parameters of a lambda given to the framework are framework values unless the table knows better.
      const params = externalLambdaParams(recv, name, index, externals, argTypes, typeArgs) ?? a.value.params.map(() => OPAQUE);
      const lambda = a.value;
      this.faking(faking, () => this.lambda(lambda, scope, params));
    });
    // `dict.TryGetValue(key, out var value)`
    if (name === 'TryGetValue' && recv.k === 'ext' && recv.args.length === 2) {
      const out = e.args[1];
      if (out?.declares === null && out.value.kind === 'name') scope.set(out.value.name, recv.args[1]!);
    }
    if (recv.k === 'code' && !this.table.hasExternalBase(recv) && !result) {
      this.unresolved++;
      return UNKNOWN;
    }
    this.external++;
    this.handoff(recv, name, argTypes, nameEnd(callee));
    return result ?? OPAQUE;
  }

  private delegateReturn(ty: Extract<Ty, { k: 'code' }>): Ty {
    const part = ty.sym.parts[0]!;
    return part.cls.returnType ? this.table.resolve(part.cls.returnType, { ctx: part.ctx, type: ty.sym, typeParams: ty.sym.typeParams }) : UNKNOWN;
  }

  private query(e: Extract<Expr, { kind: 'query' }>, scope: Scope): Ty {
    const inner = scope.child();
    const externals = (t: Ty) => this.table.externalBases(t);
    let result: Ty = UNKNOWN;
    for (const c of e.clauses) {
      switch (c.kind) {
        case 'from':
        case 'join': {
          const source = this.expr(c.source, inner);
          const elem = c.type ? this.useType(c.type) : (elementType(source, externals) ?? UNKNOWN);
          inner.set(c.name, elem);
          if (c.on) this.expr(c.on, inner);
          if (c.equals) this.expr(c.equals, inner);
          if (c.into) inner.set(c.into, ext('IEnumerable', [elem]));
          break;
        }
        case 'let':
          inner.set(c.name, this.expr(c.value, inner));
          break;
        case 'where':
          this.expr(c.value, inner);
          break;
        case 'select':
          result = this.expr(c.value, inner);
          break;
        case 'orderby':
          for (const v of c.values) this.expr(v, inner);
          break;
        case 'group': {
          const value = this.expr(c.value, inner);
          result = ext('IGrouping', [this.expr(c.by, inner), value]);
          break;
        }
        case 'into':
          inner.set(c.name, result);
          break;
      }
    }
    return ext('IEnumerable', [result]);
  }
}

/** A route template written as a plain string literal. */
function routeLiteral(e: Expr | undefined): string | null {
  return e?.kind === 'literal' && e.literal === 'string' && !e.holes?.length ? String(e.value) : null;
}

/** The strings of `new[] { "GET", "HEAD" }` or `["GET"]`, in upper case. */
function stringsIn(e: Expr | undefined): string[] {
  const items = e?.kind === 'array' ? (e.init?.items ?? []) : e?.kind === 'initializer' ? e.items : [];
  return items.flatMap((i) => (i.kind === 'literal' && i.literal === 'string' ? [String(i.value).toUpperCase()] : []));
}

/** A route prefix and a template: `/catalog` and `/products/{id}` give `/catalog/products/{id}`. */
export function joinRoute(prefix: string, template: string): string {
  const parts = [prefix, template].flatMap((p) => p.split('/')).filter(Boolean);
  return `/${parts.join('/')}`;
}

function isUpper(name: string): boolean {
  return name[0]! >= 'A' && name[0]! <= 'Z';
}

function isValueMember(m: MemberSymbol): boolean {
  return m.kind === 'property' || m.kind === 'field' || m.kind === 'const' || m.kind === 'event' || m.kind === 'enumMember';
}

/** The type an indexer of a framework type gives. */
function indexType(ty: Ty): Ty | null {
  if (ty.k === 'array') return ty.elem;
  if (ty.k !== 'ext') return null;
  if (ty.args.length === 2 && /Dictionary|SortedList/.test(ty.name)) return ty.args[1]!;
  return elementType(ty);
}

/** How well an argument fits a parameter: 2 exactly, 1 maybe, -1 not at all. */
function matchScore(arg: Ty, param: Ty): number {
  if (arg.k === 'unknown' || param.k === 'unknown' || param.k === 'param') return 0;
  if (arg.k === 'code' && param.k === 'code') return arg.sym === param.sym ? 2 : 1;
  if (arg.k === 'ext' && param.k === 'ext') {
    if (arg.name === param.name) return 2;
    if (NUMERIC.has(arg.name) && NUMERIC.has(param.name)) return 1;
    if (param.name === 'object') return 1;
    return elementType(arg) && elementType(param) ? 1 : -1;
  }
  if (param.k === 'ext' && param.name === 'object') return 1;
  if (arg.k === 'ext' && param.k === 'code') return PRIMITIVE.has(arg.name) ? -1 : 0;
  if (arg.k === 'code' && param.k === 'ext') return PRIMITIVE.has(param.name) ? -1 : 0;
  return 0;
}

const NUMERIC = new Set(['int', 'long', 'short', 'byte', 'decimal', 'double', 'float', 'uint', 'ulong', 'ushort', 'sbyte']);
const PRIMITIVE = new Set([...NUMERIC, 'string', 'bool', 'char']);

/** The number of arguments of the call whose `(` is at `open`: none for `()`, else one more than its top-level commas. */
function countArgs(tokens: { kind: string; value: string }[], open: number): number {
  const first = tokens[open + 1];
  if (!first || (first.kind === 'punct' && first.value === ')')) return 0;
  let depth = 0;
  let commas = 0;
  for (let i = open + 1; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.kind !== 'punct') continue;
    if (t.value === '(' || t.value === '[' || t.value === '{') depth++;
    else if (t.value === ')' || t.value === ']' || t.value === '}') {
      if (depth === 0) break;
      depth--;
    } else if (t.value === ',' && depth === 0) commas++;
  }
  return commas + 1;
}

/** Where a callee's name ends: the line a call is on, even in a chain spread over lines. */
function nameEnd(callee: Expr): Span {
  return { start: Math.max(callee.span.start, callee.span.end - 1), end: callee.span.end };
}

/** `sub.Received().SaveAsync(order)`: a call on NSubstitute's check of a substitute. */
function checksReceived(e: Expr): boolean {
  const callee = e.kind === 'call' ? e.callee : e;
  if (callee.kind !== 'member') return false;
  const target = callee.target;
  return target.kind === 'call' && (target.callee.kind === 'member' || target.callee.kind === 'name') && checksFake(target.callee.name);
}

/** `HttpMethod.Post` or `new HttpMethod("PATCH")`: the method in upper case. */
function httpMethodName(e: Expr | undefined): string | null {
  if (e?.kind === 'member' && e.target.kind === 'name' && e.target.name === 'HttpMethod') return e.name.toUpperCase();
  if (e?.kind === 'new' && e.args[0]?.value.kind === 'literal' && e.args[0].value.literal === 'string') return String(e.args[0].value.value).toUpperCase();
  return null;
}
